import type { SegmentProposal, SegmentResult } from '@/core/data/spark/spark.interface';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import { hasUnansweredSparkInvitation } from './spark-results-priority';

/* Structural subset of the store's SparkIntentResultsSnapshot, so this util stays
   free of a store import. */
export interface SparkOccupancySnapshot {
  intentResults: SegmentResult[];
  invitedProposals: SegmentProposal[];
  invitedChallenges: GambitSearchResultDto[];
  invitedConfirmedGambits: GambitSearchResultDto[];
  confirmedGambits: GambitSearchResultDto[];
}

export interface SparkOccupancyOptions {
  currentUserId?: string | null;
  /* Whether a proposal can reach the deck at all. Overlap rules live in the view,
     so they are handed in rather than duplicated here. */
  isProposalReachable?: (segment: SegmentResult, proposal: SegmentProposal) => boolean;
  /* Cards the user is already looking at, or is mid-action on, update in front of
     them and are therefore never news. */
  excludeProposalIds?: Set<string>;
  excludeGambitId?: string | null;
}

const getProposalOccupancyKey = (proposal: SegmentProposal): string => {
  /* Meet Anyways rides along as a flag on the voter: it is visible on the seat,
     its timestamp is not. */
  const voters = (proposal.confirmedUsers || [])
    .map((user) => `${user.id}:${user.meetAnywaysAt ? '1' : '0'}`)
    .sort()
    .join(',');

  return `proposal:${proposal.id};${voters}`;
};

const getGambitOccupancyKey = (gambit: GambitSearchResultDto): string => {
  const attendeeIds = (gambit.attendees || [])
    .map((attendee) => attendee.id)
    .sort()
    .join(',');

  return `gambit:${gambit.id};${attendeeIds}`;
};

/**
 * Identifies a day by the cards that carry people and by who sits on them, so two
 * snapshots differ only when somebody arrived, left, or moved between cards.
 *
 * Deliberately absent: suggestion segments without a proposal, because the backend
 * anchors its match window on the current hour and drops players busy in a running
 * gambit, so those cards come and go with the clock rather than with anyone's
 * action. Also absent are timestamps, chess set counts, invitation accept and
 * withdraw flags, and payload order, none of which move an avatar on screen.
 */
export const getSparkOccupancyFingerprint = (
  snapshot: SparkOccupancySnapshot,
  options: SparkOccupancyOptions = {},
): string => {
  const { currentUserId, isProposalReachable, excludeProposalIds, excludeGambitId } = options;
  const proposals = new Map<string, SegmentProposal>();

  snapshot.intentResults.forEach((segment) => {
    (segment.proposals || []).forEach((proposal) => {
      if (isProposalReachable && !isProposalReachable(segment, proposal)) {
        return;
      }

      proposals.set(proposal.id, proposal);
    });
  });

  /* Only an unanswered invitation earns a card of its own; answered ones are
     already among the segments above if they are on the deck at all. */
  (snapshot.invitedProposals || [])
    .filter((proposal) => hasUnansweredSparkInvitation(proposal, currentUserId))
    .forEach((proposal) => proposals.set(proposal.id, proposal));

  const proposalPart = [...proposals.values()]
    /* A promoted proposal leaves the deck and comes back as a gambit, so it is
       counted once, on the gambit side. */
    .filter((proposal) => !proposal.isSuccessful && !excludeProposalIds?.has(proposal.id))
    .map(getProposalOccupancyKey)
    .sort()
    .join('|');

  const gambitPart = [...snapshot.confirmedGambits, ...snapshot.invitedChallenges, ...snapshot.invitedConfirmedGambits]
    .filter((gambit) => gambit.id !== excludeGambitId)
    .map(getGambitOccupancyKey)
    .sort()
    .join('|');

  return `${proposalPart}#${gambitPart}`;
};
